import { motion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Link } from "@tanstack/react-router";
import type { Symptom } from "@/components/site/SymptomGrid";
import { courses } from "@/content/courses";
import { gapContainer, gapItem } from "@/components/site/gap-motion";

/**
 * Numbered list treatment used on the homepage Leadership Gap section.
 * Sits directly on a dark photo background: no cards, thin vertical
 * dividers between columns within each row.
 */
export function NumberedGaps({
  items,
  itemVariants = gapItem,
  theme = "dark",
  layout = "grid",
}: {
  items: readonly (Symptom & { course?: string })[];
  itemVariants?: Variants;
  theme?: "dark" | "light";
  layout?: "grid" | "stack";
}) {
  const light = theme === "light";
  const stack = layout === "stack";

  return (
    <motion.div
      variants={gapContainer}
      className={stack ? "flex flex-col" : "grid gap-y-10 sm:grid-cols-2 md:grid-cols-3 md:gap-x-0"}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {items.map((item, i) => {
        return (
          <motion.div
            key={item.title}
            variants={itemVariants}
            className={
              stack
                ? `py-7 first:pt-0 ${
                    i === 0 ? "" : light ? "border-t border-border/70" : "border-t border-white/20"
                  }`
                : `flex flex-col border-l-0 md:border-l pl-6 pr-6 md:pl-7 md:pr-8 ${
                    light ? "border-border/70" : "border-white/20"
                  }`
            }
          >
            <p
              className={`font-semibold leading-none ${
                stack ? "text-[1.125rem] md:text-[1.5rem]" : "text-4xl md:text-5xl"
              } ${light ? "text-ink" : "text-white"}`}
            >
              {String(i + 1).padStart(2, "0")}
            </p>
            <h3
              className={`mt-4 text-lg font-semibold leading-snug ${
                light ? "text-ink" : "text-white"
              }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {item.title}
            </h3>
            <p
              className={`mt-3 text-[1.1875rem] leading-relaxed md:text-base ${
                light ? "text-muted-foreground" : "text-white/75"
              }`}
            >
              {item.body}
            </p>
            {!stack && <div className="hidden md:block md:h-4" aria-hidden="true" />}
            {(() => {
              const c = item.course ? courses.find((x) => x.slug === item.course) : undefined;
              if (!c) return null;
              return (
                <Link
                  to="/course-catalog/$slug/"
                  params={{ slug: c.slug }}
                  className={`group mt-4 block rounded-lg ${stack ? "" : "md:mt-auto"} border px-4 py-3 transition-colors ${
                    light
                      ? "border-border/70 hover:border-primary/40"
                      : "border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10"
                  }`}
                >
                  <span
                    className={`block text-[0.7rem] font-semibold uppercase tracking-wider ${
                      light ? "text-primary" : "text-white"
                    }`}
                  >
                    Featured course
                  </span>
                  <span
                    className={`mt-1 block text-sm font-semibold leading-snug group-hover:underline ${
                      light ? "text-ink" : "text-white"
                    }`}
                  >
                    {c.title}
                  </span>
                </Link>
              );
            })()}
          </motion.div>
        );
      })}
    </motion.div>
  );
}
